import {
    ApplicationConfig,
    DEFAULT_CURRENCY_CODE,
    ErrorHandler,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core'
import {
    provideRouter,
    TitleStrategy,
    withComponentInputBinding,
    withHashLocation,
    withInMemoryScrolling,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router'
import { provideAnimations } from '@angular/platform-browser/animations'
import { routes } from './app.routes'
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http'
import { globalErrorHandlerInterceptor } from './core/interceptors/global-http-error-handler.interceptor'
import { provideToastr } from 'ngx-toastr'
import { CustomErrorHandler } from './core/helpers/custom-error-handler'
import { SurefirePageTitleStrategy } from './app-title-strategy'
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common'
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular'
import { UkDateParserFormatter } from './core/ng-date-formatters/uk-date-parser-formatter.service'
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap'
import { UkDateAdapter } from './core/ng-date-formatters/uk-data-adaptor.service'
import { initializeAppSettings } from './core/initializers/app-settings.initializer'
import { environment } from '../environments/environment'

// function initializer() {
//     const authService = inject(AuthorisationService)
//     const router = inject(Router)
//     const route = inject(ActivatedRoute)

//     const allowedRoutes = ['login', 'register', 'forgot-password', 'reset-password']

//     if (route.snapshot.routeConfig?.path && !allowedRoutes.includes(route.snapshot.routeConfig.path)) {
//         return of(null)
//     }

//     return authService.refreshToken().pipe(
//         catchError(() => {
//             // router.navigate(['/login'])
//             return of(null)
//         })
//     )
// }

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(initializeAppSettings),
        provideAuth0({
            domain: environment.auth.domain,
            clientId: environment.auth.clientId,
            authorizationParams: {
                audience: environment.auth.authorizationParams?.audience,
                redirect_uri: `${window.location.origin}`,
            },
            httpInterceptor: {
                allowedList: [
                    {
                        // used on registration page
                        uri: `/api/registration/config`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/registration/defaults*`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/registration`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/registration/register`,
                        allowAnonymous: true,
                    },
                    {
                        // used after registration checkout
                        uri: `/api/registration/checkout/success*`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/users/email/available`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/users/username/available`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/address-lookup/autocomplete/*`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/address-lookup/address/*`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/postcode/*`,
                        allowAnonymous: true,
                    },
                    {
                        // used on registration page
                        uri: `/api/company/email-subdomain-availability/*`,
                        allowAnonymous: true,
                    },
                    {
                        // used initial app load
                        uri: `/api/app/*`,
                        allowAnonymous: true,
                    },
                    '/api/*',
                ],
            },
            cacheLocation: 'localstorage',
            useRefreshTokens: true,
        }),
        {
            provide: LOCALE_ID,
            useValue: 'en-GB',
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'GBP',
        },
        {
            provide: DATE_PIPE_DEFAULT_OPTIONS,
            useValue: { dateformat: 'dd/MM/yyyy' },
        },
        { provide: TitleStrategy, useClass: SurefirePageTitleStrategy },
        { provide: ErrorHandler, useClass: CustomErrorHandler },
        { provide: NgbDateAdapter, useClass: UkDateAdapter },
        { provide: NgbDateParserFormatter, useClass: UkDateParserFormatter },
        provideAnimations(),
        provideToastr({ progressBar: true, extendedTimeOut: 5000 }),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(
            routes,
            withViewTransitions(),
            withComponentInputBinding(),
            withHashLocation(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' }),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
        ),
        provideHttpClient(withInterceptors([authHttpInterceptorFn, globalErrorHandlerInterceptor])),
    ],
}
